import React, { useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import useApi from '../_hooks/useApi';
import { Summary } from './Summary/Summary';
import { Insurance } from './Insurance/Insurance';
import { Goals } from './Goals/Goals';
import { Tabs } from './components/Tabs/Tabs';
import { PrimaryButton } from '../CommonComponents/Button';
import { HeaderContainer } from './MoneyGuideProStyles';
import { NetWorth } from './NetWorth/NetWorth';
import LoadingMask from '../CommonComponents/LoadingMask';
import { Tab, TabProps, Route } from './MoneyGuideProTypes';
import { clearCache } from './MoneyGuideProConstants';
import { currentRouteSelector } from '../CommonComponents/Router/Selector';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { ErrorState } from '../CommonComponents/ErrorState/ErrorState';
import { useDocumentTitle } from '../_hooks/useDocumentTitle';

const TABS_COMPONENT_MAP = {
  Summary: {
    slug: 'summary',
    component: Summary,
  },
  Insurance: {
    slug: 'insurance',
    component: Insurance,
  },
  Goals: {
    slug: 'goals',
    component: Goals,
  },
  'Net Worth': {
    slug: 'networth',
    component: NetWorth,
  },
};

const getTabContent: (tabId: number, TabsList: Tab[]) => React.FC<TabProps> = (tabId, TabsList) => {
  const selectedTab = TabsList.find(({ id }) => id === tabId);
  return selectedTab?.component || TabsList[0].component;
};

const updateUrlTabSlug = (tab: Tab, clientId: string) => {
  return ADV.util.Router.go2('MoneyGuidePro', {
    clientId,
    tabSlug: tab.slug,
    tabId: tab.id,
  });
};

interface Props {
  clientId: string;
}

export const MoneyGuidePro: React.FC<Props> = props => {
  const { clientId } = props;
  const [tabsList, setTabsList] = useState<Tab[]>();
  const [hasSsoAccess, setHasSsoAccess] = useState(false);
  const isMounted = useRef(false);
  const dispatch = useDispatch();

  const {
    params: { tabSlug, tabId: tabIdString },
  } = useSelector(currentRouteSelector) as Route;

  const tabId = parseInt(tabIdString, 10);

  const [requestMgpTabs, { loading: mgpTabLoading, errors: mgpTabErrors }] = useApi(
    urlWithParams('/Consumer/MgpReport', { clientId }),
  );

  useEffectOnce(() => {
    isMounted.current = true;

    requestMgpTabs({
      onSuccess: ({ items, hasSsoAccess: responseHasSsoAccess }) => {
        // The user left while the request was in-flight
        if (!isMounted.current) {
          return;
        }

        setHasSsoAccess(responseHasSsoAccess);
        const enhancedTabsList = items.map(tab => ({
          ...tab,
          ...TABS_COMPONENT_MAP[tab.name],
        }));

        if (tabSlug) {
          const tab = enhancedTabsList.find(({ slug }) => slug === tabSlug);
          updateUrlTabSlug(tab, clientId);
        } else {
          updateUrlTabSlug(enhancedTabsList[0], clientId);
        }

        setTabsList(enhancedTabsList);
      },
    });

    return () => {
      isMounted.current = false;
      dispatch({ type: clearCache });
    };
  });

  useDocumentTitle('Financial Planning');

  if (mgpTabErrors.length > 0) {
    return <ErrorState onRetry={requestMgpTabs} />;
  }

  if (tabSlug === '' || tabsList === undefined || mgpTabLoading) {
    return (
      <>
        <HeaderContainer>
          <LoadingMask width="400px" height="50px" isDark />
          <LoadingMask width="190px" height="50px" isDark />
        </HeaderContainer>
        <LoadingMask width="100%" height="600px" isDark />
      </>
    );
  }

  const TabComponent = getTabContent(tabId, tabsList);

  return (
    <>
      <HeaderContainer>
        <Tabs tabList={tabsList} clientId={clientId} tabId={tabId} />
        {hasSsoAccess && (
          <PrimaryButton
            onClick={() => {
              window.open(`/Consumer/MgpReport/AccessPlanDetails?clientId=${clientId}`, '_blank');
            }}
          >
            Access Plan details
          </PrimaryButton>
        )}
      </HeaderContainer>

      <TabComponent id={tabId} clientId={clientId} tabList={tabsList} />
    </>
  );
};
